import {
  Alert,
  AlertColor,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  List,
  ListItem,
  Paper,
  Snackbar,
  TextField,
  Typography,
} from "@mui/material";
import React, { useEffect, useState, useTransition } from "react";
import { IDockerEvent, IDockerProcessStatus, useDockerStatusService } from "services/registry/DockerStatusService";
import type { IEnvironment } from "../../../types/EnvironmentTypes";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";

export interface IDockerProcessStatusItemProps {
  processStatus: IDockerProcessStatus;
}

export interface IDockerEventItemProps {
  dockerEvent: IDockerEvent;
}

export const DockerProcessStatusItem = ({ processStatus }: IDockerProcessStatusItemProps) => {
  return (
    <Paper style={{ padding: "16px", marginBottom: "16px" }} variant="outlined">
      <Typography variant="h6" gutterBottom>
        {processStatus.names}
      </Typography>
      <List dense>
        <ListItem>
          <TextField label="ID" value={processStatus.id} fullWidth InputProps={{ readOnly: true }} />
        </ListItem>
        <ListItem>
          <TextField label="Image" value={processStatus.image} fullWidth InputProps={{ readOnly: true }} />
        </ListItem>
        <ListItem>
          <TextField label="Status" value={processStatus.status} fullWidth InputProps={{ readOnly: true }} />
        </ListItem>
        <ListItem>
          <TextField label="Ports" value={processStatus.ports} fullWidth InputProps={{ readOnly: true }} />
        </ListItem>
        <ListItem>
          <TextField
            label="Created At"
            value={new Date(processStatus.createdAt).toLocaleString()}
            fullWidth
            InputProps={{ readOnly: true }}
          />
        </ListItem>
      </List>
    </Paper>
  );
};

export const DockerEventItem = ({ dockerEvent }: IDockerEventItemProps) => {
  return (
    <Paper style={{ padding: "16px", marginBottom: "16px" }} variant="outlined">
      <Typography variant="h6" gutterBottom>
        {dockerEvent.containerName}
      </Typography>
      <List dense>
        <ListItem>
          <TextField label="Event" value={dockerEvent.status} fullWidth InputProps={{ readOnly: true }} />
        </ListItem>
        <ListItem>
          <TextField label="From" value={dockerEvent.from} fullWidth InputProps={{ readOnly: true }} />
        </ListItem>
        <ListItem>
          <TextField label="Scope" value={dockerEvent.scope} fullWidth InputProps={{ readOnly: true }} />
        </ListItem>
        <ListItem>
          <TextField label="Container Name" value={dockerEvent.containerName} fullWidth InputProps={{ readOnly: true }} />
        </ListItem>
        <ListItem>
          <TextField
            label="Issued At"
            value={new Date(dockerEvent.issuedAt).toLocaleString()}
            fullWidth
            InputProps={{ readOnly: true }}
          />
        </ListItem>
      </List>
    </Paper>
  );
};

export interface DockerStatusPopupProps {
  visible: boolean;
  environment?: IEnvironment;
  onClose: () => void;
}

export const DockerStatusPopup = ({ visible, environment, onClose }: DockerStatusPopupProps) => {
  useEffect(() => {
    (async () => fetchDockerStatus())();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const [loading, setLoading] = useState(false);
  const [dockerProcessStatus, setDockerProcessStatus] = useState<IDockerProcessStatus[]>([]);
  const [dockerEvents, setDockerEvents] = useState<IDockerEvent[]>([]);
  const [dockerPsOpen, setDockerPsOpen] = useState(false);
  const [dockerEventsOpen, setDockerEventsOpen] = useState(false);
  const [selectedEventTypes, setSelectedEventTypes] = useState<string[]>([]);
  const [isPending, startTransition] = useTransition();
  const [alertMessage, setAlertMessage] = useState<string | undefined>(undefined);
  const [alertSeverity, setAlertSeverity] = useState<AlertColor>("info");

  const alertCloseHandler = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === "clickaway") {
      return;
    }
    setAlertMessage(undefined);
  };

  const dockerStatusService = useDockerStatusService();

  const uniqueEventTypes = React.useMemo(() => {
    const types = new Set(dockerEvents.map((event) => event.status));
    return Array.from(types);
  }, [dockerEvents]);

  const filteredEvents = React.useMemo(() => {
    if (selectedEventTypes.length === 0) {
      return [];
    }
    return dockerEvents.filter((event) => selectedEventTypes.includes(event.status));
  }, [dockerEvents, selectedEventTypes]);

  const handleEventTypeToggle = (eventType: string) => {
    startTransition(() => {
      setSelectedEventTypes((prev) =>
        prev.includes(eventType) ? prev.filter((type) => type !== eventType) : [...prev, eventType],
      );
    });
  };

  const getDockerStatusCacheKey = (envName: string) => {
    return `docker_status_${envName}`;
  };

  const getDockerStatusCacheData = (envName: string) => {
    const cacheKey = getDockerStatusCacheKey(envName);
    const cachedData = localStorage.getItem(cacheKey);
    const now = new Date().getTime();
    const cacheExp = 5 * 60 * 1000; // 5 minutes
    if (cachedData) {
      const { timestamp, data } = JSON.parse(cachedData);
      if (now - timestamp < cacheExp) {
        return data;
      }
    }
    return null;
  };

  const fetchDockerStatus = async () => {
    setLoading(true);
    try {
      if (environment) {
        const cachedData = getDockerStatusCacheData(environment.name);
        if (cachedData) {
          setDockerProcessStatus(cachedData.processStatus);
          setDockerEvents(cachedData.events);
          return;
        }
        const res = await dockerStatusService.listDockerStatus(environment.name);
        localStorage.setItem(
          getDockerStatusCacheKey(environment.name),
          JSON.stringify({ timestamp: new Date().getTime(), data: res }),
        );
        setDockerProcessStatus(res.processStatus);
        setDockerEvents(res.events);
        const uniqueTypes = Array.from(new Set<string>(res.events.map((e: IDockerEvent) => e.status)));
        setSelectedEventTypes(uniqueTypes);
      }
    } catch (error) {
      setAlertMessage(`Error fetching Docker status: ${error}`);
      setAlertSeverity("error");
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={visible} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Docker Status for {environment?.name}</DialogTitle>
      {loading ? (
        <DialogContent>
          <CircularProgress />
          <Box>This might take a while...</Box>
        </DialogContent>
      ) : (
        <DialogContent>
          <Paper style={{ padding: "10px", marginBottom: "20px" }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              style={{ cursor: "pointer" }}
              onClick={() => setDockerPsOpen(!dockerPsOpen)}
            >
              <h3>Docker Process Status</h3>
              <IconButton size="small">{dockerPsOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton>
            </Box>
            {dockerPsOpen && (
              <>
                {dockerProcessStatus.map((processStatus) => (
                  <DockerProcessStatusItem key={processStatus.id} processStatus={processStatus} />
                ))}
              </>
            )}
          </Paper>
          <Paper style={{ padding: "10px", marginBottom: "20px" }}>
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              style={{ cursor: "pointer" }}
              onClick={() => setDockerEventsOpen(!dockerEventsOpen)}
            >
              <h3>Docker Events</h3>
              <IconButton size="small">{dockerEventsOpen ? <KeyboardArrowUpIcon /> : <KeyboardArrowDownIcon />}</IconButton>
            </Box>
            {dockerEventsOpen && (
              <>
                <FormGroup row style={{ padding: "8px 16px" }}>
                  {uniqueEventTypes.map((eventType) => (
                    <FormControlLabel
                      key={eventType}
                      control={
                        <Checkbox
                          checked={selectedEventTypes.includes(eventType)}
                          onChange={() => handleEventTypeToggle(eventType)}
                        />
                      }
                      label={eventType}
                    />
                  ))}
                </FormGroup>
                {isPending ? (
                  <Box display="flex" justifyContent="center" p={2}>
                    <CircularProgress />
                  </Box>
                ) : (
                  filteredEvents.map((dockerEvent, index) => <DockerEventItem key={index} dockerEvent={dockerEvent} />)
                )}
              </>
            )}
          </Paper>
          <Button variant="outlined" onClick={onClose} color="error" disabled={loading}>
            Close
          </Button>
        </DialogContent>
      )}
      <Snackbar open={alertMessage !== undefined} autoHideDuration={5000} onClose={alertCloseHandler}>
        <Alert onClose={alertCloseHandler} severity={alertSeverity} sx={{ width: "100%" }}>
          {alertMessage}
        </Alert>
      </Snackbar>
    </Dialog>
  );
};
